import React, { FC, useMemo, useState } from 'react';
import { Box, CircularProgress, Container, Paper, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useGetResourceQuery } from '../healthWalletSlice';
import { formatDate, recordStats } from '../healthWalletConstants';
import type { GroupedSummary } from '../healthWalletConstants';
import type { SectionProps } from './TabRenderer';
import { procCategoryKey, procCategoryMeta, PROC_CATEGORY_META, isResourceUpcoming, isPlannedOrder, moodBadgeLabel } from './classify';
import type { ProcCategoryKey } from './classify';
import { V1_ACCENT, V1_ACCENT_TINT, V1_ACCENT_BORDER } from './theme';
import { V1SearchField } from './V1SearchField';

// ============================================================================
// Procedures tab sections (design: Polygon Health Trends Redesign/Procedures
// Desktop.dc.html, in the DESIGN palette). Data = get_patient_resource
// ('procedures') name-groups + code/codeSystem/org fields; categories via the
// CPT-range classifier (classify.ts). SIX sections — the design's separate
// "Infusions" category was MERGED into Treatments (Leo 2026-07-13: admin codes
// are treatment delivery; "Infusion" already means the drug on Medications and
// the visit on Encounters).
// ============================================================================

const PAGE_WIDTH_SX = { maxWidth: { xs: '100%', sm: '94%' } };
const CARD_LIMIT = 6;
export const CAT_ORDER: ProcCategoryKey[] = ['treatment', 'lab', 'imaging', 'biopsy', 'education', 'other'];

const useProcedureGroups = () => {
  const { data, isLoading } = useGetResourceQuery('procedures');
  const groups: GroupedSummary[] = useMemo(
    () => (data ? [...data.named, ...(data.other ? [{ ...data.other, name: data.other.name || 'Other / Uncategorized' }] : [])] : []),
    [data]
  );
  return { groups, totalRecords: data?.totalRecords ?? 0, rawRecords: data?.rawRecords ?? 0, isLoading };
};

// --- 1. Hero -----------------------------------------------------------------
export const ProceduresHero: FC<SectionProps> = () => {
  const { groups, rawRecords, isLoading } = useProcedureGroups();
  // Hero describes the PERFORMED-historical procedures ONLY (Leo 2026-07-26): planned Orders live in their own
  // section and Upcoming has its own tab, so the sentence's X/Y AND the tiles both count only performed —
  // the sentence total now equals the tile sum. Mirrors ProceduresBody's `performed`.
  const performed = useMemo(() => groups.filter((g) => !isResourceUpcoming(g, 'procedures') && !isPlannedOrder(g)), [groups]);
  const { x, y, grouped } = recordStats(performed.reduce((a, g) => a + g.count, 0), performed.length, rawRecords);
  const counts = useMemo(() => {
    const c: Record<ProcCategoryKey, number> = { treatment: 0, lab: 0, imaging: 0, biopsy: 0, education: 0, other: 0 };
    performed.forEach((g) => { c[procCategoryKey(g)] += 1; });
    return c;
  }, [performed]);
  if (isLoading) {
    return <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2, display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Container>;
  }
  const tile = (value: number, label: string, bg: string, fg: string) => (
    <Box sx={{ width: { xs: 92, sm: 104 }, flexShrink: 0, bgcolor: bg, borderRadius: '15px', p: '16px 10px', textAlign: 'center' }}>
      <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: fg, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: fg, mt: 0.75, fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
      <Paper elevation={0} sx={{ borderRadius: '20px', p: '24px 26px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: V1_ACCENT, textTransform: 'uppercase', mb: 1.25 }}>
            Your record at a glance
          </Typography>
          <Typography sx={{ fontSize: 21, fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.2px', maxWidth: 580 }}>
            {grouped ? (
              <>Your record contains <strong>{x.toLocaleString('en-US')}</strong> procedure entries, grouped into <strong>{y.toLocaleString('en-US')}</strong> procedures and tests.</>
            ) : (
              <>Your record contains <strong>{y.toLocaleString('en-US')}</strong> {y === 1 ? 'procedure or test' : 'procedures and tests'}.</>
            )}
          </Typography>
        </Box>
        {/* Present-only over ALL categories from the shared PROC_CATEGORY_META (single source w/ the sections),
            so biopsy/education/other no longer silently drop off the hero. */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {CAT_ORDER.filter((k) => counts[k] > 0).map((k) => (
            <React.Fragment key={k}>{tile(counts[k], PROC_CATEGORY_META[k].pill, PROC_CATEGORY_META[k].bg, PROC_CATEGORY_META[k].fg)}</React.Fragment>
          ))}
        </Box>
      </Paper>
    </Container>
  );
};

// --- 2. Explainer — static "you may see many records" (reworded to avoid asserting a threshold);
// hidden only when the tab has NO records.
export const ProceduresExplainer: FC<SectionProps> = () => {
  const { totalRecords } = useProcedureGroups();
  if (totalRecords === 0) return null;
  return (
  <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
    <Box sx={{ bgcolor: V1_ACCENT_TINT, borderRadius: '16px', p: '16px 20px', display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 'none', width: 26, height: 26, borderRadius: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <InfoOutlinedIcon sx={{ color: V1_ACCENT, fontSize: 18 }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 650, mb: 0.4 }}>You may see many records</Typography>
        <Typography sx={{ fontSize: 13, color: '#4a4a52', lineHeight: 1.5, maxWidth: 820 }}>
          Providers document a procedure entry for many routine steps of care — every blood draw, every infusion session,
          every imaging study. A high count usually reflects ongoing care over time, not many different operations.
          Sections start collapsed so you can open just what you want to see.
        </Typography>
      </Box>
    </Box>
  </Container>
  );
};

// --- Card ---------------------------------------------------------------------
const CARD_DATE = (d: string) => (d ? formatDate(d) : '—');

const ProcedureCard: FC<{ g: GroupedSummary; onOpen: () => void }> = ({ g, onOpen }) => {
  const meta = procCategoryMeta(g);
  // Planned/ordered/… badge — ONLY on a FUTURE planned procedure: isResourceUpcoming gates on intent/mood
  // PLUS a future date, so the chip appears exclusively in the Upcoming tab. Undated/past orders (e.g. Alan's
  // standing INT lab/imaging orders) + performed procedures get NO chip. Blue (distinct from the purple
  // Treatment category chip) so the two chips never read as one.
  const mood = isResourceUpcoming(g, 'procedures') ? moodBadgeLabel(g) : null;
  return (
    <Box onClick={onOpen} sx={{
      bgcolor: '#fdfcff', border: '1px solid #ece9f4', borderRadius: '14px', p: 2.25, cursor: 'pointer',
      '&:hover': { borderColor: V1_ACCENT_BORDER, bgcolor: 'white' },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.25 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 650, letterSpacing: '-0.2px', lineHeight: 1.3, minWidth: 0 }}>{g.name}</Typography>
        <Box sx={{ flex: 'none', display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {mood && (
            <Box sx={{ fontSize: '10.5px', fontWeight: 700, px: 1.25, py: 0.5, borderRadius: '20px', bgcolor: '#eef3fb', color: '#2f5fb0', whiteSpace: 'nowrap' }}>
              {mood[0].toUpperCase() + mood.slice(1)}
            </Box>
          )}
          <Box sx={{ fontSize: '10.5px', fontWeight: 700, px: 1.25, py: 0.5, borderRadius: '20px', bgcolor: meta.bg, color: meta.fg, whiteSpace: 'nowrap' }}>
            {meta.label}
          </Box>
        </Box>
      </Box>
      {g.org ? (
        <Typography sx={{ fontSize: 12, color: '#6b6b70', mt: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.org}</Typography>
      ) : null}
      <Box sx={{ display: 'flex', gap: 2.5, mt: 1.75 }}>
        {/* Same calendar day for first & last → show one "Date recorded" (CARD_DATE is day-level). */}
        {g.firstDate && CARD_DATE(g.firstDate) === CARD_DATE(g.lastDate) ? (
          <Box><Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 600, whiteSpace: 'nowrap' }}>Date recorded</Typography><Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.25 }}>{CARD_DATE(g.firstDate)}</Typography></Box>
        ) : (
          <>
            <Box><Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 600, whiteSpace: 'nowrap' }}>First documented</Typography><Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.25 }}>{CARD_DATE(g.firstDate)}</Typography></Box>
            <Box><Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 600, whiteSpace: 'nowrap' }}>Most recent</Typography><Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.25 }}>{CARD_DATE(g.lastDate)}</Typography></Box>
          </>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.75, pt: 1.5, borderTop: '1px solid #f0eef6' }}>
        <Typography sx={{ fontSize: 12, color: '#6b6b70' }}>Documented {g.count.toLocaleString('en-US')} {g.count === 1 ? 'time' : 'times'}</Typography>
        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: V1_ACCENT, whiteSpace: 'nowrap' }}>View history ›</Typography>
      </Box>
    </Box>
  );
};

// --- 3+4+5. Filters + category sections + cards ---------------------------------
type FilterKey = 'all' | 'orders' | ProcCategoryKey;

// `upcomingOnly` (default false) mirrors EncountersBody: the historical Procedures tab EXCLUDES
// planned procedures; the Upcoming tab reuses THIS component with upcomingOnly to render the same
// cards for not-yet-performed procedures (RTK dedupes the shared 'procedures' read).
export const ProceduresBody: FC<SectionProps & { upcomingOnly?: boolean }> = ({ openDetail, upcomingOnly }) => {
  const { groups: allGroups, isLoading } = useProcedureGroups();
  // SINGLE filter point — flip the group set here so all downstream bucketing/sections/cards stay untouched.
  const groups = useMemo(
    () => allGroups.filter((g) => (upcomingOnly ? isResourceUpcoming(g, 'procedures') : !isResourceUpcoming(g, 'procedures'))),
    [allGroups, upcomingOnly]
  );
  // Historical view: split planned/ordered requests (ServiceRequests — undated/past → NOT upcoming) into their own
  // "Orders" section; the category buckets below hold only PERFORMED procedures. Upcoming tab = already future orders,
  // so no split. See classify.isPlannedOrder / project_hw_upcoming_resource_classification.
  const performed = useMemo(() => (upcomingOnly ? groups : groups.filter((g) => !isPlannedOrder(g))), [groups, upcomingOnly]);
  const orders = useMemo(() => (upcomingOnly ? [] : groups.filter((g) => isPlannedOrder(g))), [groups, upcomingOnly]);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [showAll, setShowAll] = useState<Record<string, boolean>>({});

  const q = search.trim().toLowerCase();
  const matches = (g: GroupedSummary) => !q || (g.name || 'other').toLowerCase().includes(q);

  const buckets = useMemo(() => {
    const b: Record<ProcCategoryKey, GroupedSummary[]> = { treatment: [], lab: [], imaging: [], biopsy: [], education: [], other: [] };
    [...performed].sort((a, c) => c.count - a.count).forEach((g) => { b[procCategoryKey(g)].push(g); });
    return b;
  }, [performed]);

  if (isLoading) return null; // hero shows the spinner

  const pill = (key: FilterKey, label: string, disabledWhenEmpty = true) => {
    if (disabledWhenEmpty && key !== 'all') {
      const n = key === 'orders' ? orders.length : buckets[key as ProcCategoryKey].length;
      if (n === 0) return null;
    }
    return (
      <Box key={key} onClick={() => setFilter(key)} sx={{
        fontSize: 13, fontWeight: 600, px: 2, py: 1, borderRadius: '20px', cursor: 'pointer',
        bgcolor: filter === key ? V1_ACCENT : 'white', color: filter === key ? 'white' : '#3a3a3c',
        border: `1px solid ${filter === key ? V1_ACCENT : '#e0e0e6'}`,
      }}>{label}</Box>
    );
  };

  // Default-open = the FIRST NON-EMPTY section in display order (Leo 2026-07-14).
  const firstNonEmpty = CAT_ORDER.find((k) => buckets[k].length > 0);
  const defaultOpen = (key: string) => key === firstNonEmpty;

  const section = (key: ProcCategoryKey) => {
    const list = buckets[key];
    const visible = (filter === 'all' || filter === key) && list.filter(matches).length > 0;
    if (!visible) return null;
    const full = list.filter(matches);
    const forced = !!q || filter === key;
    const isOpen = forced ? true : (open[key] ?? defaultOpen(key));
    const all = !!q || !!showAll[key];
    const cards = isOpen ? (all ? full : full.slice(0, CARD_LIMIT)) : [];
    return (
      <Paper key={key} elevation={0} sx={{ borderRadius: '18px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', overflow: 'hidden' }}>
        <Box onClick={() => setOpen((s) => ({ ...s, [key]: !(s[key] ?? defaultOpen(key)) }))} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '18px 22px', cursor: 'pointer' }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PROC_CATEGORY_META[key].dot, flex: 'none' }} />
          <Typography component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px', flex: 1 }}>
            {PROC_CATEGORY_META[key].title}
          </Typography>
          <Box sx={{ fontSize: '12.5px', fontWeight: 700, color: '#6b6b70', bgcolor: '#f0f0f3', px: 1.4, py: 0.5, borderRadius: '20px' }}>{full.length}</Box>
          <Typography sx={{ fontSize: 13, color: '#8e8e93', width: 16, textAlign: 'center' }}>{isOpen ? '▾' : '▸'}</Typography>
        </Box>
        {isOpen && (
          <Box sx={{ px: 2.75, pb: 2.75 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 1.75 }}>
              {cards.map((g, i) => <ProcedureCard key={`${g.name}-${i}`} g={g} onOpen={() => openDetail(g)} />)}
            </Box>
            {full.length > CARD_LIMIT && (
              <Typography onClick={() => setShowAll((s) => ({ ...s, [key]: !s[key] }))}
                sx={{ textAlign: 'center', mt: 2, fontSize: 13, fontWeight: 600, color: V1_ACCENT, cursor: 'pointer' }}>
                {all ? 'Show fewer' : `Show all ${full.length} →`}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  // "Orders" section (historical view only) — ordered/planned tests & services, NOT performed records.
  // Collapsed by default (secondary to performed procedures); shown only in the 'all' filter.
  const renderOrders = () => {
    // Newest-ordered first (most-recent order date desc); undated orders sink to the bottom, then by the
    // group's existing count order. ISO date strings compare lexically. (Leo 2026-07-23 — ~2/3 are dated.)
    const full = orders.filter(matches).slice().sort((a, b) => (b.lastDate ?? '').localeCompare(a.lastDate ?? ''));
    if (full.length === 0) return null;
    const forced = !!q || filter === 'orders';        // clicking the Orders pill force-opens it (mirrors categories)
    const isOpen = forced ? true : (open['orders'] ?? false);
    const all = !!q || !!showAll['orders'];
    const cards = isOpen ? (all ? full : full.slice(0, CARD_LIMIT)) : [];
    return (
      <Paper key="orders" elevation={0} sx={{ borderRadius: '18px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', overflow: 'hidden' }}>
        <Box onClick={() => setOpen((s) => ({ ...s, orders: !(s['orders'] ?? false) }))} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '18px 22px', cursor: 'pointer' }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2f5fb0', flex: 'none' }} />
          <Typography component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px', flex: 1 }}>Orders</Typography>
          <Box sx={{ fontSize: '12.5px', fontWeight: 700, color: '#6b6b70', bgcolor: '#f0f0f3', px: 1.4, py: 0.5, borderRadius: '20px' }}>{full.length}</Box>
          <Typography sx={{ fontSize: 13, color: '#8e8e93', width: 16, textAlign: 'center' }}>{isOpen ? '▾' : '▸'}</Typography>
        </Box>
        {isOpen && (
          <Box sx={{ px: 2.75, pb: 2.75 }}>
            <Typography sx={{ fontSize: '12.5px', color: '#8e8e93', mb: 1.75, lineHeight: 1.5 }}>
              Tests and services that were ordered or planned — not a record that they were performed.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 1.75 }}>
              {cards.map((g, i) => <ProcedureCard key={`ord-${g.name}-${i}`} g={g} onOpen={() => openDetail(g)} />)}
            </Box>
            {full.length > CARD_LIMIT && (
              <Typography onClick={() => setShowAll((s) => ({ ...s, orders: !s['orders'] }))}
                sx={{ textAlign: 'center', mt: 2, fontSize: 13, fontWeight: 600, color: V1_ACCENT, cursor: 'pointer' }}>
                {all ? 'Show fewer' : `Show all ${full.length} →`}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  const noResults = !!q && !CAT_ORDER.some((k) => buckets[k].some(matches)) && !orders.some(matches);

  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2.5, pb: 12 }}>
      <V1SearchField value={search} onChange={setSearch} placeholder="Search procedures and tests" />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {pill('all', 'All', false)}
        {CAT_ORDER.map((k) => pill(k, PROC_CATEGORY_META[k].pill))}
        {!upcomingOnly && pill('orders', 'Orders')}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        {CAT_ORDER.map((k) => section(k))}
        {!upcomingOnly && (filter === 'all' || filter === 'orders') && renderOrders()}
      </Box>
      {noResults && (
        <Typography sx={{ p: '40px 26px', textAlign: 'center', color: '#8e8e93', fontSize: 15 }}>
          No records match &ldquo;{search}&rdquo;.
        </Typography>
      )}
      <Box sx={{ mt: 4.5, pt: 2.75, borderTop: '1px solid #e2e2e8', textAlign: 'center' }}>
        <Typography sx={{ fontSize: 12, color: '#aeaeb2', lineHeight: 1.55, maxWidth: 660, mx: 'auto' }}>
          This page organizes the procedure and test records reported by your healthcare providers. It presents your health
          record as documented and does not interpret how or why a procedure was performed.
        </Typography>
      </Box>
    </Container>
  );
};
